import { z } from "zod";
import { PAGE_SIZE_MAX, searchParam } from "./pagination";

/**
 * `/card-handover-locations` — where accreditation cards are handed over: the
 * Accreditation Centre and the welcome desks at the entry points (airports,
 * land crossings, ferry terminals). Wire shapes only (snake_case).
 *
 * The list only. A handover records a place from this list or as free text,
 * plus sender and receiver; that comes with the card milestone.
 */

export const CARD_HANDOVER_LOCATION_SORT_KEYS = ["sort_order", "code", "name", "created_at"] as const;
export type CardHandoverLocationSortKey = (typeof CARD_HANDOVER_LOCATION_SORT_KEYS)[number];

/** A short admin list (tens of rows): one page of 50 normally holds all of it. */
export const CARD_HANDOVER_LOCATION_PAGE_SIZE_DEFAULT = 50;

export const CARD_HANDOVER_LOCATION_CODE_MAX = 20;
export const CARD_HANDOVER_LOCATION_NAME_MAX = 200;
export const CARD_HANDOVER_LOCATION_ADDRESS_MAX = 500;

/** `"true"` / `"false"` off the query string — never `z.coerce.boolean()` (`Boolean("false") === true`). */
const booleanParam = z.enum(["true", "false"]);

export const listCardHandoverLocationsQuery = z.object({
	page: z.coerce.number().int().min(1).default(1),
	page_size: z.coerce.number().int().min(1).max(PAGE_SIZE_MAX).default(CARD_HANDOVER_LOCATION_PAGE_SIZE_DEFAULT),
	/** Matches name, code and address. */
	search: searchParam.optional(),
	is_active: booleanParam.optional(),
	sort: z.enum(CARD_HANDOVER_LOCATION_SORT_KEYS).optional(),
	dir: z.enum(["asc", "desc"]).optional(),
});
export type ListCardHandoverLocationsQuery = z.input<typeof listCardHandoverLocationsQuery>;

/** One row of `GET /card-handover-locations`; also the detail and what a write returns. */
export interface CardHandoverLocationListItem {
	card_handover_location_id: string;
	/** Short code (ACC, KUL T1) in the case typed. Unique when set. */
	code: string | null;
	name: string;
	address: string | null;
	sort_order: number;
	/** Hidden from pickers when false. */
	is_active: boolean;
	created_at: string;
	updated_at: string;
}

/** `GET /card-handover-locations/options` — active rows, in sort order. */
export interface CardHandoverLocationOption {
	card_handover_location_id: string;
	code: string | null;
	name: string;
	address: string | null;
}

/** `GET /card-handover-locations/stats`. */
export interface CardHandoverLocationStats {
	total: number;
	active: number;
	inactive: number;
}

// ── Write requests ──────────────────────────────────────────────────────────

export interface CardHandoverLocationFormMessages {
	nameRequired: string;
	sortOrderInvalid: string;
}

export const enCardHandoverLocationFormMessages: CardHandoverLocationFormMessages = {
	nameRequired: "Enter a name",
	sortOrderInvalid: "Enter a whole number, 0 or more",
};

/** Optional inputs bind as `""`; the `to*Body` helpers drop or null them. Every field trims FIRST (bug 5). */
export function makeCardHandoverLocationForm(m: CardHandoverLocationFormMessages = enCardHandoverLocationFormMessages) {
	return z.object({
		code: z.string().trim().max(CARD_HANDOVER_LOCATION_CODE_MAX),
		name: z.string().trim().min(1, m.nameRequired).max(CARD_HANDOVER_LOCATION_NAME_MAX),
		address: z.string().trim().max(CARD_HANDOVER_LOCATION_ADDRESS_MAX),
		sort_order: z.coerce.number({ invalid_type_error: m.sortOrderInvalid }).int(m.sortOrderInvalid).min(0, m.sortOrderInvalid).max(9999, m.sortOrderInvalid),
		is_active: z.boolean(),
	});
}
export const cardHandoverLocationForm = makeCardHandoverLocationForm();
export type CardHandoverLocationForm = z.infer<typeof cardHandoverLocationForm>;

export interface CreateCardHandoverLocationBody {
	code?: string;
	name: string;
	address?: string;
	sort_order?: number;
	is_active?: boolean;
}

/** `null` clears a field; omitted leaves it alone. */
export interface UpdateCardHandoverLocationBody {
	code?: string | null;
	name?: string;
	address?: string | null;
	sort_order?: number;
	is_active?: boolean;
	/** Headline for this change in the Activity feed. */
	reason?: string;
}

export const toCreateCardHandoverLocationBody = (f: CardHandoverLocationForm): CreateCardHandoverLocationBody => ({
	name: f.name,
	...(f.code ? { code: f.code } : {}),
	...(f.address ? { address: f.address } : {}),
	sort_order: f.sort_order,
	is_active: f.is_active,
});

/** Only what actually changed, so the audit row's `changed_fields` names the edit. */
export function toUpdateCardHandoverLocationBody(f: CardHandoverLocationForm, before: CardHandoverLocationListItem): UpdateCardHandoverLocationBody {
	const body: UpdateCardHandoverLocationBody = {};
	if (f.name !== before.name) body.name = f.name;
	if (f.sort_order !== before.sort_order) body.sort_order = f.sort_order;
	if (f.is_active !== before.is_active) body.is_active = f.is_active;
	const diffNullable = (next: string, prev: string | null) => (next === "" ? (prev === null ? undefined : null) : next === prev ? undefined : next);
	const code = diffNullable(f.code, before.code);
	if (code !== undefined) body.code = code;
	const address = diffNullable(f.address, before.address);
	if (address !== undefined) body.address = address;
	return body;
}
